/**
 * Change history for the service-mapping store.
 *
 * Mirrors cms/history.ts's design (record enough to undo without replaying an
 * inverse operation) but simpler in one respect: services/store.ts never
 * cascade-deletes. Deleting an entity detaches references to it elsewhere
 * (nulling scalars, pruning id arrays) rather than deleting those referrers,
 * so a delete's snapshot is always just the one entity — no subtree to walk.
 *
 * Stored in localStorage alongside the service store. Capped, same rationale
 * as the article side: entity JSON is the bulk of the payload.
 */

import { ulid } from "ulid"
import { type ServiceStore, type EntityKind, BUCKET, displayName } from "./types"
import { pushHistory } from "../shared/persistence/historySync"

export type ChangeAction = "create" | "update" | "delete" | "translate" | "import" | "revert"

export interface ChangeEntry {
  id: string
  at: number
  action: ChangeAction
  /** Whether the change came from the agent or a person editing directly. */
  actor: "agent" | "user"
  /** Attribution. For agent changes this is the signed-in user who prompted it. */
  authorName?: string
  authorEmail?: string
  authorId?: string
  /** Model id, when the agent made the change. */
  viaModel?: string
  /**
   * Absent for a bulk "import" entry — a CSV/Excel import can touch hundreds
   * of entities in one run, so it is logged as a single line rather than
   * attributed to one entity. It is not individually restorable; see
   * revertEntry, which rejects entries with no entityId.
   */
  entityKind?: EntityKind
  entityId?: string
  /** Human-readable name at the time of the change. */
  entityName: string
  locale?: string
  /** One-line description shown in the log. */
  summary: string
  /** The entity as it was before this change. Absent for create (nothing existed). */
  before?: Record<string, any>
  /** The entity as it was after this change. Absent for delete. */
  after?: Record<string, any>
  /** True once this entry has been reverted, so the UI can grey it out. */
  reverted?: boolean
}

/** Logs a bulk operation (e.g. a CSV import) as one line, with no single entity to restore. */
export function makeBulkEntry(args: {
  actor: "agent" | "user"
  authorName?: string
  authorEmail?: string
  authorId?: string
  viaModel?: string
  summary: string
}): ChangeEntry {
  return { ...args, action: "import", entityName: args.summary, id: ulid(), at: Date.now() }
}

const HISTORY_KEY_PREFIX = "svc-cms-history"
const DEFAULT_DB = "default"
const MAX_ENTRIES = 200

/** dbId matches the id passed to store.ts's loadStore/saveStore, so a team's history stays with its data. */
function historyKeyFor(dbId: string) { return dbId === DEFAULT_DB ? HISTORY_KEY_PREFIX : `${HISTORY_KEY_PREFIX}:${dbId}` }

/*
 * In-memory cache backing the synchronous history API.
 *
 * Entries are written to Postgres (see shared/persistence/historySync) and
 * read back on load; this map is what the synchronous readers see in between.
 * Not localStorage -- a change log that only the browser which made the
 * change can read is not an audit trail.
 */
const cache = new Map<string, ChangeEntry[]>()

export function loadHistory(dbId = DEFAULT_DB): ChangeEntry[] {
  return cache.get(historyKeyFor(dbId)) ?? []
}

export function saveHistory(entries: ChangeEntry[], dbId = DEFAULT_DB): void {
  pushHistory("service-map", entries)
  cache.set(historyKeyFor(dbId), entries.slice(-MAX_ENTRIES))
}

export function clearHistory(dbId = DEFAULT_DB): void {
  cache.delete(historyKeyFor(dbId))
}

export function makeEntry(args: Omit<ChangeEntry, "id" | "at">): ChangeEntry {
  return { ...args, id: ulid(), at: Date.now() }
}

function getFromBucket(store: ServiceStore, kind: EntityKind, id: string): Record<string, any> | undefined {
  return (store[BUCKET[kind]] as Record<string, any>)[id]
}

function putInBucket(store: ServiceStore, kind: EntityKind, entity: Record<string, any>): void {
  ;(store[BUCKET[kind]] as Record<string, any>)[entity.id] = entity
}

function dropFromBucket(store: ServiceStore, kind: EntityKind, id: string): void {
  delete (store[BUCKET[kind]] as Record<string, any>)[id]
}

/**
 * Applies a revert: puts the recorded `before` state back (or removes the
 * entity, for undoing a create). Returns the new store and a description, or
 * an error string when the entry can't be reverted.
 */
export function revertEntry(
  store: ServiceStore,
  entry: ChangeEntry
): { store: ServiceStore; description: string } | { error: string } {
  if (entry.reverted) return { error: "That change has already been reverted." }
  if (!entry.entityKind || !entry.entityId) {
    return { error: "This entry covers a bulk operation, not one entity, so it can't be reverted as a single step." }
  }

  const next: ServiceStore = JSON.parse(JSON.stringify(store))

  if (entry.action === "create") {
    dropFromBucket(next, entry.entityKind, entry.entityId)
    return { store: next, description: `Removed "${entry.entityName}" (undo of create)` }
  }

  if (!entry.before) {
    return { error: "This entry has no saved snapshot, so it can't be reverted." }
  }

  if (entry.action === "delete") {
    putInBucket(next, entry.entityKind, entry.before)
    return { store: next, description: `Restored "${entry.entityName}"` }
  }

  // update / translate: restore the prior state.
  putInBucket(next, entry.entityKind, entry.before)
  return { store: next, description: `Reverted ${entry.action} on "${entry.entityName}"` }
}

/** Version id for the state recorded BEFORE an entry, distinct from its after-state. */
export const priorVersionId = (entryId: string) => `${entryId}:before`

/**
 * The version timeline for one entity: every recorded state of it, newest
 * first. Each version is the `after` snapshot of a change (or the `before`
 * snapshot of the earliest recorded change, for the state that preceded it).
 */
export interface EntityVersion {
  /**
   * Identifies this VERSION, not the entry it came from — one entry can yield
   * two versions (its `after` state, and its `before` state when it is the
   * earliest recorded change), and those must be separately addressable or
   * the prior state can never be restored. The prior-state row is suffixed
   * `:before`; use `sourceEntryId` for the raw entry id.
   */
  entryId: string
  /** The history entry this version was derived from. */
  sourceEntryId: string
  at: number
  action: ChangeAction
  author: string
  actor: "agent" | "user"
  summary: string
  /** The entity as it was at this point. Absent when the snapshot was trimmed. */
  entity?: Record<string, any>
  /** True for the state BEFORE the entry, rather than after it. */
  isPriorState?: boolean
}

export function versionsForEntity(entries: ChangeEntry[], entityId: string): EntityVersion[] {
  // Exclude BOTH the synthetic "revert" log lines (which carry no snapshot of
  // their own worth restoring) and entries that have since been reverted — an
  // undone change's snapshot is no longer a state this entity was ever left in.
  const relevant = entries.filter(e => e.entityId === entityId && e.action !== "revert" && !e.reverted)
  const out: EntityVersion[] = []

  for (const e of relevant) {
    const author = e.authorName || e.authorEmail || (e.actor === "agent" ? "agent" : "unknown")
    if (e.after) {
      out.push({
        entryId: e.id, sourceEntryId: e.id, at: e.at, action: e.action,
        author, actor: e.actor, summary: e.summary, entity: e.after,
      })
    }
  }

  // Prepend the state before the earliest change, so the original is restorable.
  // Its id must differ from that entry's after-state id, or lookups by id
  // resolve to the wrong version and the original becomes unreachable.
  const earliest = relevant[0]
  if (earliest?.before) {
    const author = earliest.authorName || earliest.authorEmail || (earliest.actor === "agent" ? "agent" : "unknown")
    out.unshift({
      entryId: priorVersionId(earliest.id), sourceEntryId: earliest.id,
      at: earliest.at, action: earliest.action, author,
      actor: earliest.actor, summary: `State before: ${earliest.summary}`,
      entity: earliest.before, isPriorState: true,
    })
  }

  return out.reverse() // newest first
}

/** Writes one recorded version of an entity back into the store. */
export function restoreEntityVersion(store: ServiceStore, kind: EntityKind, entity: Record<string, any>): ServiceStore {
  const next: ServiceStore = JSON.parse(JSON.stringify(store))
  putInBucket(next, kind, entity)
  return next
}

/** Reads an entity's current display name, falling back to its id if it's gone. */
